import React from "react";
import { motion } from "framer-motion";
import { Sun, Moon } from "lucide-react";
import { useTheme } from "../../context/ThemeContext";

/**
 * ThemeSwitch — Apple-style pill toggle
 * ─────────────────────────────────────────────────────────────
 * A premium, accessible day/night toggle. Renders a small pill
 * with a sliding "thumb" that animates between Sun (light) and
 * Moon (dark). Uses the design-system tokens so it auto-adapts
 * to either theme.
 *
 *   <ThemeSwitch />
 *   <ThemeSwitch size="sm" />
 *
 * Props
 *   size: "sm" | "md" (default "md")
 *   className: optional extra classes
 */
const SIZES = {
  sm: { wrap: "h-7 w-12", thumb: 22, icon: "w-3 h-3" },
  md: { wrap: "h-8 w-14", thumb: 26, icon: "w-3.5 h-3.5" },
};

export default function ThemeSwitch({ size = "md", className = "" }) {
  const { isDark, toggleTheme } = useTheme();
  const dim = SIZES[size] || SIZES.md;

  return (
    <button
      type="button"
      role="switch"
      aria-checked={isDark}
      aria-label={isDark ? "Switch to light theme" : "Switch to dark theme"}
      onClick={toggleTheme}
      data-testid="theme-switch"
      data-state={isDark ? "dark" : "light"}
      className={[
        "relative inline-flex items-center shrink-0 rounded-full px-1",
        "border border-[var(--border-color)] hover:border-[var(--border-strong)]",
        "bg-[var(--bg-secondary)]",
        "transition-[border-color,background-color] duration-200 ease-out",
        "focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)]",
        dim.wrap,
        className,
      ].join(" ")}
    >
      {/* Track icons (faint, indicate destinations) */}
      <Sun
        className={`${dim.icon} absolute left-1.5 text-[var(--warning)] opacity-${isDark ? "30" : "0"} transition-opacity duration-200 pointer-events-none`}
        aria-hidden="true"
      />
      <Moon
        className={`${dim.icon} absolute right-1.5 text-[var(--accent-primary)] opacity-${isDark ? "0" : "30"} transition-opacity duration-200 pointer-events-none`}
        aria-hidden="true"
      />

      {/* Sliding thumb */}
      <motion.span
        layout
        transition={{ type: "spring", stiffness: 360, damping: 30, mass: 0.6 }}
        className="relative z-10 flex items-center justify-center rounded-full shadow-sm"
        style={{
          width: dim.thumb,
          height: dim.thumb,
          background: isDark
            ? "linear-gradient(180deg, #1E3A8A 0%, #1E293B 100%)"
            : "linear-gradient(180deg, #FFFFFF 0%, #F4F4F5 100%)",
          marginLeft: isDark ? "auto" : 0,
          marginRight: isDark ? 0 : "auto",
          boxShadow: isDark
            ? "0 1px 2px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.08)"
            : "0 1px 3px rgba(15,23,42,0.18), inset 0 1px 0 rgba(255,255,255,0.6)",
        }}
      >
        {isDark ? (
          <Moon className={`${dim.icon} text-[var(--accent-primary-hover)]`} strokeWidth={2.2} />
        ) : (
          <Sun className={`${dim.icon} text-[var(--warning)]`} strokeWidth={2.4} />
        )}
      </motion.span>
    </button>
  );
}
